import React, { useMemo, useRef } from 'react';
import { PamGrid, useGridConfigs } from '@1823-partners/core';
import { SetFilterModule } from 'ag-grid-enterprise';

import api from '../../api';
import { filesColumns } from './remittanceColumns';

const getFileRowId = (data) => String(data.SOURCE_KEY);

/**
 * The servicer files stored for one month, and the button that adds another.
 *
 * The upload button lives on this grid's toolbar rather than beside the run controls because what
 * it changes is this list. Reading the list before starting a run is the point: a file the parser
 * cannot read contributes no cash at all, and seeing that here costs nothing, while seeing it
 * afterwards means reading it off a run's integrity counts and re-running the month.
 */
const RemittanceFilesGrid = ({
  files,
  isLoading,
  canIngest,
  onUpload,
  selectedSourceKey,
  onSelectFile,
  onDeleteFile,
  height = 240,
}) => {
  const gridRef = useRef(null);

  const columnDefs = useMemo(() => filesColumns(), []);

  /*
   * One row at a time, deliberately. The toolbar's bulk-delete slot would take a selection, but a
   * file leaving a month changes what that month reconciles, so each one is worth naming.
   *
   * The permission reason wins over the selection hint: with no grant, selecting a row will not
   * help, so telling somebody to select one would send them down a dead end.
   */
  const customToolbarButtons = useMemo(() => (canIngest ? [{
    label: 'Delete file',
    onClick: onDeleteFile,
    disabled: !selectedSourceKey || !!isLoading,
    tooltip: selectedSourceKey
      ? 'Retire this file so future runs stop reading it'
      : 'Select a file to delete',
  }] : []), [canIngest, selectedSourceKey, isLoading, onDeleteFile]);

  const gridConfigs = useGridConfigs(api, {
    app: 'recon',
    gridId: 'remittance-files',
    gridApiRef: gridRef,
  });

  return (
    <PamGrid
      ref={gridRef}
      rowData={files}
      columnDefs={columnDefs}
      getRowId={getFileRowId}
      height={`${height}px`}
      showToolbar
      title="Servicer files"
      // acceptedFileTypes is left at the toolbar's default '.csv,.xlsx,.xls', which already matches
      // the engine's ACCEPTED_SUFFIXES exactly. Restating it here would be a second copy to keep
      // in step with pam-master.
      showUpload={canIngest}
      onUpload={onUpload}
      uploadLabel="Upload servicer file"
      uploadTooltip="Store one servicer remittance file (.xlsx, .xls or .csv) against the selected month. Re-uploading the same file name replaces the stored version."
      showExport
      exportConfig={{
        fileName: `CMLRemittanceFiles_${new Date().toISOString().split('T')[0]}.xlsx`,
        sheetName: 'Files',
      }}
      customToolbarButtons={customToolbarButtons}
      onGridReady={gridConfigs.onGridReady}
      onFilterChanged={gridConfigs.onGridStateChanged}
      onRowClicked={(event) => onSelectFile?.(event.data)}
      isLoading={isLoading}
      loadingMessage="Loading servicer files..."
      modules={[SetFilterModule]}
    />
  );
};

export default RemittanceFilesGrid;
